import * as React from "react";
import {
    Panel,
    PanelType,
    Label,
    Stack,
    Spinner,
    SpinnerSize,
    DefaultButton,
    MessageBar,
    MessageBarType,
    Icon,
    FontSizes,
    mergeStyles,
} from "@fluentui/react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { getSubscriptionHistory, SubscriptionHistoryItem } from "../../utilities/helpers/ApiHelper";
import { Pill, Tone } from "../Pill";

interface SubscriptionHistoryPanelProps {
    isOpen: boolean;
    subscriptionId: number;
    onDismiss: () => void;
}

const timelineRowStyle = mergeStyles({
    borderLeft: "2px solid var(--db-pill-info-border)",
    paddingLeft: 12,
    marginLeft: 6,
    paddingTop: 8,
    paddingBottom: 8,
});

const statusTone = (status: string): Tone => {
    switch (status) {
        case "applied": return "success";
        case "submitted": return "warning";
        case "failed": return "danger";
        default: return "neutral";
    }
};

// Human-readable labels for the machine EventKind tokens written by the API. The stored
// values stay stable (machine tokens); only the display text is friendly. "SubscriptionProjected"
// is the legacy kind that the 2026-05-29 backfill splits into Created / Updated — kept here so
// rows still read sensibly during the deploy window before the backfill runs.
// Unknown kinds fall back to the raw token.
const eventKindLabels: Record<string, string> = {
    QuantityChangeRequested: "Quantity change requested",
    CancelRequested: "Cancellation requested",
    ReactivationRequested: "Reactivation requested",
    StatusChanged: "Status changed",
    SeatAssigned: "Seat assigned",
    SeatRevoked: "Seat revoked",
    Created: "Created",
    Updated: "Updated",
    SubscriptionProjected: "Updated",
    Canceled: "Canceled",
    CancelScheduled: "Cancellation scheduled",
    CancelReverted: "Cancellation reverted",
    CatalogSuspended: "Suspended (plan removed)",
    InvoiceSuspended: "Suspended (unpaid invoice)",
    SeatAutoRevoked: "Seat auto-revoked",
    SeatAutoMigrated: "Seat auto-moved",
};

const eventKindLabel = (kind: string): string => eventKindLabels[kind] ?? kind;

// Try to parse JSON; return null on any failure so the UI shows the raw string instead
// of throwing. Some rows may carry truncation-marker JSON ({_truncated:true,...}) which
// still parses fine — the marker just surfaces in the display.
const tryParseJson = (raw?: string | null): any | null => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const formatTimestamp = (iso: string): string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
};

const SubscriptionHistoryPanel: React.FC<SubscriptionHistoryPanelProps> = ({
    isOpen,
    subscriptionId,
    onDismiss,
}) => {
    const languageStrings = React.useContext(LanguageContext);
    const [items, setItems] = React.useState<SubscriptionHistoryItem[]>([]);
    const [nextBefore, setNextBefore] = React.useState<number | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const [expandedRowIds, setExpandedRowIds] = React.useState<Set<number>>(new Set());
    const [error, setError] = React.useState<string | null>(null);

    const loadFirst = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        const res = await getSubscriptionHistory(subscriptionId, null, 50);
        if (!res) {
            setError(languageStrings.SubscriptionHistoryLoadFailed ?? "Failed to load history.");
            setItems([]);
            setNextBefore(null);
        } else {
            setItems(res.items ?? []);
            setNextBefore(res.nextBefore ?? null);
        }
        setIsLoading(false);
    }, [subscriptionId, languageStrings]);

    const loadMore = React.useCallback(async () => {
        if (nextBefore == null) return;
        setIsLoadingMore(true);
        const res = await getSubscriptionHistory(subscriptionId, nextBefore, 50);
        if (res) {
            setItems(prev => [...prev, ...(res.items ?? [])]);
            setNextBefore(res.nextBefore ?? null);
        }
        setIsLoadingMore(false);
    }, [subscriptionId, nextBefore]);

    React.useEffect(() => {
        if (isOpen) {
            loadFirst();
        }
    }, [isOpen, loadFirst]);

    const toggleExpanded = (id: number) => {
        setExpandedRowIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const renderItem = (item: SubscriptionHistoryItem) => {
        const expanded = expandedRowIds.has(item.id);
        const oldVals = tryParseJson(item.oldValuesJson);
        const newVals = tryParseJson(item.newValuesJson);
        const details = tryParseJson(item.detailsJson);
        return (
            <div key={item.id} className={timelineRowStyle}>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Stack horizontal wrap tokens={{ childrenGap: 6 }} verticalAlign="center">
                        <Pill tone="info">{eventKindLabel(item.eventKind)}</Pill>
                        <Pill tone="neutral">{item.source}</Pill>
                        <Pill tone={statusTone(item.status)}>{item.status}</Pill>
                    </Stack>
                    <div style={{ fontSize: FontSizes.size12, color: "var(--db-fg-muted)" }}>
                        {formatTimestamp(item.occurredAt)}
                        {item.actorEmail ? ` · ${item.actorEmail}` : ""}
                        {item.stripeOpVersion != null ? ` · op v${item.stripeOpVersion}` : ""}
                        {item.stripeEventId ? ` · ${item.stripeEventType ?? "stripe"}` : ""}
                    </div>
                    {item.failureReason && (
                        <div style={{ color: "var(--db-error-fg)", fontSize: FontSizes.size12 }}>
                            {item.failureReason}
                        </div>
                    )}
                    <DefaultButton
                        text={expanded ? (languageStrings.HideDetails ?? "Hide details") : (languageStrings.ShowDetails ?? "Show details")}
                        iconProps={{ iconName: expanded ? "ChevronUp" : "ChevronDown" }}
                        styles={{ root: { height: 24, padding: "0 8px" }, label: { fontSize: FontSizes.size12 } }}
                        onClick={() => toggleExpanded(item.id)}
                    />
                    {expanded && (
                        <Stack tokens={{ childrenGap: 4 }} style={{ marginTop: 4 }}>
                            {oldVals && (
                                <div>
                                    <Label styles={{ root: { fontSize: FontSizes.size12, padding: 0 } }}>{languageStrings.OldValues ?? "Old"}</Label>
                                    <pre style={{ margin: 0, fontSize: FontSizes.size12, backgroundColor: "var(--db-surface)", padding: 6, borderRadius: 2, overflow: "auto" }}>{JSON.stringify(oldVals, null, 2)}</pre>
                                </div>
                            )}
                            {newVals && (
                                <div>
                                    <Label styles={{ root: { fontSize: FontSizes.size12, padding: 0 } }}>{languageStrings.NewValues ?? "New"}</Label>
                                    <pre style={{ margin: 0, fontSize: FontSizes.size12, backgroundColor: "var(--db-surface)", padding: 6, borderRadius: 2, overflow: "auto" }}>{JSON.stringify(newVals, null, 2)}</pre>
                                </div>
                            )}
                            {details && (
                                <div>
                                    <Label styles={{ root: { fontSize: FontSizes.size12, padding: 0 } }}>{languageStrings.Details ?? "Details"}</Label>
                                    <pre style={{ margin: 0, fontSize: FontSizes.size12, backgroundColor: "var(--db-surface)", padding: 6, borderRadius: 2, overflow: "auto" }}>{JSON.stringify(details, null, 2)}</pre>
                                </div>
                            )}
                        </Stack>
                    )}
                </Stack>
            </div>
        );
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            headerText={languageStrings.SubscriptionHistory ?? "Subscription history"}
            type={PanelType.medium}
            closeButtonAriaLabel={languageStrings.Close ?? "Close"}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                nextBefore != null
                    ? <DefaultButton
                        text={isLoadingMore ? (languageStrings.Loading ?? "Loading...") : (languageStrings.LoadMore ?? "Load more")}
                        iconProps={{ iconName: "ChevronDownMed" }}
                        disabled={isLoadingMore}
                        onClick={loadMore}
                    />
                    : null
            )}
        >
            <Stack tokens={{ childrenGap: 12 }} style={{ paddingTop: 8 }}>
                {isLoading && (
                    <Spinner size={SpinnerSize.medium} label={languageStrings.Loading ?? "Loading..."} />
                )}
                {!isLoading && error && (
                    <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
                )}
                {!isLoading && !error && items.length === 0 && (
                    <div style={{ textAlign: "center", padding: 24, color: "var(--db-fg-muted)" }}>
                        <Icon iconName="History" style={{ fontSize: 24, marginBottom: 8 }} />
                        <div>{languageStrings.NoSubscriptionHistory ?? "No history yet."}</div>
                    </div>
                )}
                {!isLoading && items.map(renderItem)}
            </Stack>
        </Panel>
    );
};

export default SubscriptionHistoryPanel;
